import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LogoutService } from 'app/core/auth/logout.service';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface TenantPayload {
    name: string;
    code: string;
}

interface Domain {
    id: string;
    domain: string;
}

interface Tenant {
    id: string;
    name: string;
    code: string;
    domains?: Domain[];
}

interface DomainPayload {
    domain: string;
}

@Component({
    selector: 'app-tenant',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './tenant.component.html',
    styleUrl: './tenant.component.scss',
})
export class TenantComponent {
    isDropdownOpen = false;
    isLoggingOut = false;
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    isDomainModalOpen = false;

    Tenant: Tenant[] = [];

    tenantForm: TenantPayload = {
        name: '',
        code: '',
    };

    domainForm: DomainPayload = {
        domain: '',
    }

    selectedTenantId: string = '';

    constructor(
        private logoutService: LogoutService,
        private apiService: ApiService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.getTenant();
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Tenants
    async getTenant(): Promise<void> {
        try {
            const response = await this.apiService.get(
                '/api/v1/manage/tenant/list'
            );
            // console.log(response);
            this.Tenant = response.data.list;
        } catch (error) {
            console.error('Error fetching tenants:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memuat data tenant',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // GET - Single Tenant (untuk edit)
    async getTenantById(tenantId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/tenant/${tenantId}`
            );
            // console.log(response);

            // Populate form dengan data tenant
            this.tenantForm = {
                name: response.data.name,
                code: response.data.code,
            };

            this.selectedTenantId = tenantId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            console.error('Error fetching tenant:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message ||'Gagal memuat data tenant',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // POST - Create Tenant
    async createTenant(): Promise<void> {
        if (!this.validateTenantForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/manage/tenant', this.tenantForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Tenant berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getTenant();
        } catch (error: any) {
            console.error('Error creating tenant:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan tenant',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT/PATCH - Update Tenant
    async updateTenant(): Promise<void> {
        if (!this.validateTenantForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(
                `/api/v1/manage/tenant/${this.selectedTenantId}`,
                this.tenantForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Tenant berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getTenant(); // Refresh list
        } catch (error: any) {
            console.error('Error updating tenant:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate tenant',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    async setDomain(): Promise<void> {
        if (!this.validateDomainForm()) {
            return;
        }

        this.isSubmitting = true;
        try {
            await this.apiService.post(`/api/v1/manage/tenant/${this.selectedTenantId}/set-domain`, this.domainForm);

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Domain berhasil diset',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeDomainModal();
            this.getTenant();
        } catch (error: any) {
            console.error('Error setting domain:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal set domain',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // Unset Domain Tenant
    async unsetDomain(tenantId: string, domainId: string, domainName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Unset Domain',
            text: `Apakah Anda yakin ingin menghapus domain "${domainName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus Domain',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.deleteWithPayload(
                    `/api/v1/manage/tenant/${tenantId}/unset-domain`,
                    { domain: domainName }
                );

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Domain berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getTenant(); // Refresh list
            } catch (error: any) {
                console.error('Error unsetting domain:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus domain',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // DELETE - Delete Tenant
    async deleteTenant(tenantId: string, tenantName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus tenant "${tenantName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete('/api/v1/manage/tenant', tenantId);

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Tenant berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                this.getTenant(); // Refresh list
            } catch (error: any) {
                console.error('Error deleting tenant:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus tenant',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== MODAL OPERATIONS ====================

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(tenantId: string): void {
        this.getTenantById(tenantId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    openDomainModal(tenantId: string): void {
        this.selectedTenantId = tenantId;
        // console.log(this.selectedTenantId);
        this.domainForm.domain = '';
        this.isDomainModalOpen = true;
    }

    closeDomainModal(): void {
        this.isDomainModalOpen = false;
        this.domainForm.domain = '';
        this.selectedTenantId = '';
    }

    resetForm(): void {
        this.tenantForm = {
            name: '',
            code: '',
        };
        this.selectedTenantId = '';
    }

    // ==================== FORM VALIDATION ====================

    validateTenantForm(): boolean {
        if (!this.tenantForm.name || !this.tenantForm.code) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Name dan Code wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Validasi code format (hanya huruf besar dan angka, tanpa spasi)
        const codeRegex = /^[A-Z0-9]+$/;
        if (!codeRegex.test(this.tenantForm.code)) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Code hanya boleh mengandung huruf besar dan angka, tanpa spasi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    validateDomainForm(): boolean {
        if (!this.domainForm.domain || this.domainForm.domain.trim() === '') {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Domain wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateTenant();
        } else {
            await this.createTenant();
        }
    }
}
